<template>
  <div class="mapa-calor">
    <div class="mc-toolbar noprint">
      <Dropdown
        v-model="zonaFiltro"
        :options="zonaOptions"
        optionLabel="label"
        optionValue="value"
        class="mc-periodo"
        data-e2e="dropdown-congregacion"
      />
      <Dropdown
        v-model="periodoMeses"
        :options="periodos"
        optionLabel="label"
        optionValue="value"
        class="mc-periodo"
      />
      <MultiSelect
        v-model="horarioFiltro"
        :options="horarioOptions"
        optionLabel="label"
        optionValue="value"
        placeholder="Horario"
        class="mc-periodo"
        display="chip"
      />
      <div class="mc-stats">
        <span class="mc-chip">{{ heat.totalManzanas }} manzanas</span>
        <span class="mc-chip mc-chip-nunca">{{ heat.nuncaTrabajadas }} nunca trabajadas</span>
        <span class="mc-chip mc-chip-max">máx. {{ heat.maxVeces }} {{ heat.maxVeces === 1 ? "vez" : "veces" }}</span>
      </div>
    </div>

    <div class="relative">
      <MapaBase
        :editing="false"
        :saveCenter="false"
        :search="false"
        :height="mapHeight"
        @ready="onReady"
      />
      <div class="leyenda-mapa">
        <div class="leyenda-titulo">Veces predicada</div>
        <div class="leyenda-escala">
          <div v-for="(c, i) in RAMPA" :key="i" class="leyenda-step" :style="{ background: c }"></div>
        </div>
        <div class="leyenda-labels"><span>1</span><span>{{ RAMPA.length }}+</span></div>
        <div class="leyenda-item">
          <span class="leyenda-color leyenda-nunca"></span>
          Nunca trabajada
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onUnmounted, ref, watch } from "vue";
import MapaBase from "@/components/MapaBase/MapaBase.vue";
import { useTerritoriosStore } from "@/store/territorios";
import { useTrabajoStore } from "@/store/useTrabajoStore";
import { useScreenStore } from "@/store/useScreenStore";
import { TURNO_OPTIONS } from "@/utils/turno";

const territorios = useTerritoriosStore();
const trabajo = useTrabajoStore();
const screen = useScreenStore();

// Rampa secuencial de un solo tono (azul, claro→oscuro): escala absoluta de
// "veces predicada" (1..7+), estable al cambiar el período filtrado.
const RAMPA = ["#cde2fb", "#9ec5f4", "#6da7ec", "#3987e5", "#2a78d6", "#1c5cab", "#0d366b"];
const ESTILO_NUNCA = {
  color: "#9ca3af",
  fillColor: "#e5e7eb",
  fillOpacity: 0.15,
  weight: 2,
  dashArray: "6, 5",
};

const periodos = [
  { label: "Todo el historial", value: 0 },
  { label: "Últimos 24 meses", value: 24 },
  { label: "Últimos 12 meses", value: 12 },
  { label: "Últimos 6 meses", value: 6 },
  { label: "Últimos 3 meses", value: 3 },
];
const periodoMeses = ref(0);

// Las zonas del catálogo son las congregaciones a las que pertenece cada territorio
const TODAS = "__todas__";
const zonaFiltro = ref(TODAS);
const zonaOptions = computed(() => [
  { label: "Todas las congregaciones", value: TODAS },
  ...[...new Set(territorios.list.map((t) => t.zona).filter(Boolean))]
    .sort()
    .map((z) => ({ label: z, value: z })),
]);
const pasaFiltro = (terr) => zonaFiltro.value === TODAS || terr.zona === zonaFiltro.value;

// Horario de la salida asociada al registro (mañana/tarde/noche) o "sin_horario"
// para los informes históricos que no tienen turno guardado.
const horarioOptions = [...TURNO_OPTIONS, { label: "Sin horario", value: "sin_horario" }];
const horarioFiltro = ref(horarioOptions.map((o) => o.value));

const mapHeight = computed(() =>
  screen.isMobile ? "calc(100vh - 165px)" : "calc(100vh - 64px)"
);

const cutoffTs = computed(() => {
  if (!periodoMeses.value) return 0;
  const d = new Date();
  d.setMonth(d.getMonth() - periodoMeses.value);
  return d.getTime();
});

// Misma tolerancia que useTrabajoStore.parseTimestamp: ms numéricos o ISO string
const parseFecha = (fecha) => {
  if (fecha === null || fecha === undefined || fecha === "") return null;
  const d = /^\d+$/.test(String(fecha)) ? new Date(parseInt(fecha)) : new Date(fecha);
  const t = d.getTime();
  return isNaN(t) ? null : t;
};

// Conteo de veces trabajada por manzana (y última fecha), por territorio,
// dentro del período seleccionado.
const heat = computed(() => {
  const cutoff = cutoffTs.value;
  const porTerritorio = {};
  let maxVeces = 0;
  let totalManzanas = 0;
  let nuncaTrabajadas = 0;

  for (const terr of territorios.list) {
    if (!terr.uuid || !terr.limits?.length || !pasaFiltro(terr)) continue;
    const detail = trabajo.data[terr.uuid];
    const veces = {};
    const ultima = {};

    for (const r of detail?.registros || []) {
      const ts = parseFecha(r.fecha);
      if (ts === null || ts < cutoff) continue;
      if (!horarioFiltro.value.includes(r.turno || "sin_horario")) continue;
      for (const mz of (r.manzanas || []).map(Number)) {
        veces[mz] = (veces[mz] || 0) + 1;
        if (!ultima[mz] || ts > ultima[mz]) ultima[mz] = ts;
      }
    }

    const manzanasTotal = Math.max((terr.mzNumbers?.length || 1) - 1, 0);
    let suma = 0;
    for (let mz = 1; mz <= manzanasTotal; mz++) {
      const v = veces[mz] || 0;
      suma += v;
      if (v > maxVeces) maxVeces = v;
      if (v === 0) nuncaTrabajadas++;
    }
    totalManzanas += manzanasTotal;
    porTerritorio[terr.uuid] = {
      veces,
      ultima,
      suma,
      promedio: manzanasTotal ? suma / manzanasTotal : 0,
    };
  }

  return { porTerritorio, maxVeces, totalManzanas, nuncaTrabajadas };
});

const nivelDe = (count) => Math.min(count, RAMPA.length) - 1;

const estiloTerritorio = (info) => {
  if (!info || info.suma === 0) return ESTILO_NUNCA;
  const nivel = nivelDe(Math.max(1, Math.round(info.promedio)));
  return {
    color: RAMPA[Math.min(nivel + 2, RAMPA.length - 1)],
    fillColor: RAMPA[nivel],
    fillOpacity: 0.3,
    weight: 2,
    dashArray: null,
  };
};

const escapeHtml = (s) =>
  String(s ?? "").replace(/[&<>"']/g, (c) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  }[c]));

let L = null;
let mapInstance = null;
let polygonGroup = null;
let mzGroup = null;
let maskLayer = null;

// Anillo exterior que cubre el mundo; los territorios visibles se agregan como
// agujeros, dejando una máscara oscura sobre todo lo que no es territorio.
const WORLD_RING = [
  [-90, -360],
  [90, -360],
  [90, 360],
  [-90, 360],
];
// Clave del último encuadre: re-encuadra al cambiar de congregación, no de período
let fittedKey = null;

const buildManzanaIcon = (count) => {
  let style;
  if (count === 0) {
    style = "background: #f4f4f2; border: 1px solid #9ca3af;";
  } else {
    const nivel = nivelDe(count);
    const borde = RAMPA[Math.min(nivel + 2, RAMPA.length - 1)];
    style = `background: ${RAMPA[nivel]}; border: 1px solid ${borde};`;
  }
  // Punto visible de 10px dentro de un área de hover de 16px
  return L.divIcon({
    className: "mc-punto-wrap",
    iconSize: [16, 16],
    iconAnchor: [8, 8],
    html: `<div class="mc-punto" style="${style}"></div>`,
  });
};

const tooltipManzana = (nombre, mzIdx, count, ultimaTs) => {
  const veces =
    count === 0
      ? "Nunca trabajada"
      : `Trabajada ${count} ${count === 1 ? "vez" : "veces"}`;
  const ultima = ultimaTs ? `<br>Última vez: ${trabajo.getDate(ultimaTs)}` : "";
  return `<b>${escapeHtml(nombre)}</b> · Manzana ${mzIdx}<br>${veces}${ultima}`;
};

const tooltipTerritorio = (terr, info) => {
  const promedio = info ? info.promedio : 0;
  return `
    <div class="territorio-tooltip-body">
      <div class="tt-title">${escapeHtml(terr.territorio_nombre)}</div>
      ${terr.referencia ? `<div class="tt-ref">${escapeHtml(terr.referencia)}</div>` : ""}
      <div class="tt-row">Promedio: ${promedio.toFixed(1)} veces por manzana</div>
    </div>
  `;
};

const draw = () => {
  if (!mapInstance || !L) return;

  if (polygonGroup) polygonGroup.remove();
  if (mzGroup) mzGroup.remove();
  if (maskLayer) maskLayer.remove();

  const polys = [];
  const mzMarkers = [];
  const bounds = [];
  const holes = [];
  const { porTerritorio } = heat.value;

  for (const terr of territorios.listWithName) {
    if (!terr.uuid || !terr.limits?.length || !pasaFiltro(terr)) continue;
    const info = porTerritorio[terr.uuid];

    const poly = L.polygon(terr.limits, estiloTerritorio(info));
    poly.bindTooltip(tooltipTerritorio(terr, info), {
      sticky: true,
      className: "territorio-tooltip",
    });
    polys.push(poly);
    bounds.push(...terr.limits);
    holes.push(terr.limits);

    for (const key in terr.mzNumbers || {}) {
      const mzIdx = parseInt(key);
      if (mzIdx === 0) continue;
      const count = info?.veces[mzIdx] || 0;
      const marker = L.marker(terr.mzNumbers[key][0], {
        icon: buildManzanaIcon(count),
      });
      marker.bindTooltip(tooltipManzana(terr.territorio_nombre, mzIdx, count, info?.ultima[mzIdx]), {
        className: "territorio-tooltip",
      });
      mzMarkers.push(marker);
    }
  }

  maskLayer = L.polygon([WORLD_RING, ...holes], {
    stroke: false,
    fillColor: "#000000",
    fillOpacity: 0.3,
    interactive: false,
  }).addTo(mapInstance);
  polygonGroup = L.featureGroup(polys).addTo(mapInstance);
  mzGroup = L.layerGroup(mzMarkers).addTo(mapInstance);
  const key = zonaFiltro.value;
  if (bounds.length && fittedKey !== key) {
    mapInstance.fitBounds(bounds, { padding: [20, 20] });
    fittedKey = key;
  }
};

const onReady = ({ m, map }) => {
  L = m().L;
  mapInstance = map();
  draw();
};

watch(heat, draw);

onUnmounted(() => {
  if (polygonGroup) polygonGroup.remove();
  if (mzGroup) mzGroup.remove();
  if (maskLayer) maskLayer.remove();
});
</script>

<style scoped>
.mc-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-2, 8px);
  padding: var(--space-2, 8px) var(--space-3, 12px);
  background: var(--bg-secondary, #fff);
  border-bottom: 1px solid var(--separator, #e5e7eb);
}
.mc-periodo {
  min-width: 190px;
  text-align: left;
}
.mc-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-left: auto;
}
.mc-chip {
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 0.8em;
  background: var(--fill-quaternary, #f3f4f6);
  color: var(--label-primary, #1f2937);
  white-space: nowrap;
}
.mc-chip-nunca {
  background: #f4f4f2;
  border: 1px dashed #9ca3af;
  color: #52514e;
}
.mc-chip-max {
  background: #cde2fb;
  color: #0d366b;
}

.leyenda-mapa {
  position: absolute;
  bottom: 24px;
  right: 10px;
  z-index: 1000;
  background: rgba(255, 255, 255, 0.92);
  border-radius: 6px;
  padding: 8px 10px;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.15);
  font-size: 0.8em;
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: #1f2937;
  text-align: left;
}
.leyenda-titulo {
  font-weight: 600;
}
.leyenda-escala {
  display: flex;
  gap: 2px;
}
.leyenda-step {
  width: 18px;
  height: 12px;
  border-radius: 2px;
}
.leyenda-labels {
  display: flex;
  justify-content: space-between;
  color: #6b7280;
  font-size: 0.9em;
  line-height: 1;
}
.leyenda-item {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
}
.leyenda-color {
  width: 14px;
  height: 14px;
  border-radius: 3px;
  flex-shrink: 0;
}
.leyenda-nunca {
  background: #f4f4f2;
  border: 2px dashed #9ca3af;
  border-radius: 50%;
}

:deep(.mc-punto-wrap) {
  background: transparent;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
}
:deep(.mc-punto) {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  box-sizing: content-box;
  box-shadow: 0 0 2px rgba(0, 0, 0, 0.4);
}

:deep(.territorio-tooltip) {
  padding: 6px 8px;
}
:deep(.territorio-tooltip-body) {
  color: #1f2937;
  font-size: 0.8em;
  line-height: 1.4;
}
:deep(.tt-title) {
  font-weight: bold;
  font-size: 1.05em;
}
:deep(.tt-ref) {
  color: #6b7280;
  margin-bottom: 2px;
}
:deep(.tt-row) {
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}
</style>
